import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { observer } from "mobx-react";
import { checkTeamSubscriptionActive } from "Utils/commonAPI";
import userDetail from "Stores/userDetail";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import Button from "Components/common/Button";
import dialerStoreData from "Stores/DialerData";
import LinkedinCredits from "./Credits/LinkedinCredits";
import EmailCredits from "./Credits/EmailCredits";
import { upgradePopupState } from "Components/Paywall/UpgradePopup";
import { buyCreditsPopupState } from "Pages/Dashboard/settings/BuyCreditsPopup";
import { transactionAnimationState } from "Components/common/TransactionAnimation";
import { toasterState } from "Components/common/toaster";
import { RECHARGE_ERR_MSG } from "Model/model";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import PaywallUtils from "Utils/PaywallUtils";
import { useHistory } from "react-router-dom";

const defaultObject = {
  annualPhoneCreditsUsed: 0,
  dailyEmailCreditsUsed: 0,
  dailyLimit: false,
  dailyPhoneCreditsUsed: 0,
  emailCreditsDailyLimit: 0,
  emailCreditsMonthlyLimit: 0,
  emailCreditsReduced: 0,
  monthlyCreditsResetDate: "",
  monthlyEmailCreditsUsed: 0,
  monthlyPhoneCreditsUsed: 0,
  phoneCreditsAnnualLimit: 0,
  phoneCreditsDailyLimit: 0,
  phoneCreditsMonthlyLimit: 0,
  phoneCreditsReduced: 0
};

export const CreditsUsage = withRouter(
  observer(props => {
    const {
      planName = "",
      inFreePlan = false,
      emailValidationEnabled = false
    } = userDetail?.userFeatureFlag || {};

    const isFreeUser = inFreePlan;

    const [data, setData] = useState(defaultObject);
    const [loader, setLoader] = useState(false);
    const [addCreditsLoader, setAddCreditsLoader] = useState(false);
    const [creditsCardLoader, setCreditsCardLoader] = useState(true);
    const history = useHistory();

    const getCreditsUsage = async () => {
      if (await checkTeamSubscriptionActive()) {
        if (!loader) {
          try {
            const config = {
              url: URLS.getCreditsUsage
            };
            const res = await makeApi(config);
            if (res?.data && Object.keys(res?.data)?.length > 0) {
              setData(res?.data);
              setCreditsCardLoader(false);
            }
          } catch (err) {
            console.error(err, "Getting Credit Usage ERROR!");
          }
        }
      }
      return;
    };

    const addEmailPhoneNumberCredits = () => {
      if (isFreeUser) {
        props.history.push("/pricing");
      } else {
        PaywallUtils.upgradeUtilsFunction(
          "data_AddCreditsToDataSearch",
          true,
          planName,
          inFreePlan
        );
      }
    };

    const showRechargeErrCodeMsg = response => {
      let code = response?.data?.responseCode || response?.data?.status;
      if (code === 207) {
        toasterState.setToastMsg("UNAUTHORIZED Access", "fail");
      } else {
        let content = RECHARGE_ERR_MSG[code] || "";
        if (content) {
          confirmationPopupState.setPopupValues({
            content,
            actionBtnText: "Ok",
            needCancelBtn: false,
            dynamicContent: true,
            callback: () => confirmationPopupState.setShowPopup(false)
          });
          confirmationPopupState.setShowPopup(true);
        } else {
          toasterState.setToastMsg(
            "Something went wrong. Please try again later",
            "failure"
          );
        }
      }
    };

    const purchaseEmailValidationCredits = async (amount = 0) => {
      if (amount) {
        try {
          const config = {
            url: URLS.purchaseEmailValidationCredits,
            method: "POST",
            data: { amount }
          };
          let response = await makeApi(config);
          if (response?.data?.responseCode === 200) {
            await Utils.reloadFeatureFlag(false);
            toasterState.setToastMsg(
              "Credits added to your account successfully",
              "success"
            );
          } else {
            showRechargeErrCodeMsg(response);
          }
        } catch (e) {
          console.error("Recharge email validation credits failed", e);
        }
      }
    };

    const purchaseCreditsCbk = async (amount = 0, stripeToken = "") => {
      if (amount && !stripeToken) {
        transactionAnimationState.setShowPopup(true);
        await purchaseEmailValidationCredits(amount);
        transactionAnimationState.setShowPopup(false);
      } else {
        showRechargeErrCodeMsg(201);
      }
    };

    const addEmailValidationCredits = async () => {
      if (await checkTeamSubscriptionActive()) {
        if (emailValidationEnabled && !isFreeUser) {
          setAddCreditsLoader(true);
          const cardResponse = Utils.checkCustomerIdAvailable();
          if (cardResponse === 200) {
            buyCreditsPopupState.setNewPayment(false);
            buyCreditsPopupState.setCallBack(purchaseCreditsCbk);
            buyCreditsPopupState.setPopupValue({
              type: "EV_CREDITS"
            });
            buyCreditsPopupState.setShowPopup(true);
          } else if (Utils.isEligibleToShowPaymentPopup(cardResponse)) {
            buyCreditsPopupState.setNewPayment(true);
            buyCreditsPopupState.setCallBack(purchaseCreditsCbk);
            buyCreditsPopupState.setPopupValue({
              type: "EV_CREDITS"
            });
            buyCreditsPopupState.setShowPopup(true);
          } else {
            PaywallUtils.upgradeUtilsFunction(
              cardResponse === 203
                ? "data_AddCreditsToEV"
                : "emailValidationUpgrade",
              true,
              planName,
              inFreePlan
            );
          }
        }
        setAddCreditsLoader(false);
      }
    };

    const purchaseDialerCreditsCbk = async (
      selectedRechargeAmount,
      stripeToken
    ) => {
      if (selectedRechargeAmount && !stripeToken) {
        transactionAnimationState.setShowPopup(true);
        const payload = { amount: selectedRechargeAmount };
        await dialerStoreData.purchaseCredits(payload);
        transactionAnimationState.setShowPopup(false);
      } else if (selectedRechargeAmount && stripeToken) {
        transactionAnimationState.setShowPopup(true);
        const payload = { chargeAmount: selectedRechargeAmount, stripeToken };
        await dialerStoreData.purchaseCreditsWithoutSubscription(payload);
        transactionAnimationState.setShowPopup(false);
      }
    };

    const buyCredits = async () => {
      if (await checkTeamSubscriptionActive()) {
        const cardResponse = Utils.checkCustomerIdAvailable();
        if (cardResponse === 200) {
          buyCreditsPopupState.setNewPayment(false);
          buyCreditsPopupState.setCallBack(purchaseDialerCreditsCbk);
          buyCreditsPopupState.setPopupValue({ type: "DIALER" });
          buyCreditsPopupState.setShowPopup(true);
        } else if (Utils.isEligibleToShowPaymentPopup(cardResponse)) {
          buyCreditsPopupState.setNewPayment(true);
          buyCreditsPopupState.setCallBack(purchaseDialerCreditsCbk);
          buyCreditsPopupState.setPopupValue({ type: "DIALER" });
          buyCreditsPopupState.setShowPopup(true);
        } else if (cardResponse === 203) {
          upgradePopupState.setPopupValues({
            type: "data_AddCreditsToDialer"
          });
          upgradePopupState.setPopup(true);
        }
      }
    };

    const navigateToPricing = () => {
      history.push("/pricing");
    };

    const buttonObject = {
      refresh: {
        id: "refreshCreditsUsage",
        name: "refreshCreditsUsage",
        btnText: "Refresh",
        type: "button",
        loader,
        icon: true,
        iconName: "refresh",
        className: `refreshCreditsUsage ${loader ? "loading" : ""}`,
        click: () => getInitPage(true)
      },
      addEmailCreditsBtn: {
        id: "addEmailCreditsBtn",
        name: "addEmailCreditsBtn",
        btnText: isFreeUser ? "Upgrade" : "Add Credits",
        type: "button",
        className: "addCreditsBtn",
        click: addEmailPhoneNumberCredits
      },
      addPhoneCreditsBtn: {
        id: "addPhoneCreditsBtn",
        name: "addPhoneCreditsBtn",
        btnText: isFreeUser ? "Upgrade" : "Add Credits",
        type: "button",
        className: "addCreditsBtn",
        click: addEmailPhoneNumberCredits
      },
      addEmailValidationCreditsBtn: {
        id: "addEmailValidationCreditsBtn",
        name: "addEmailValidationCreditsBtn",
        btnText:
          isFreeUser || !emailValidationEnabled ? "Upgrade" : "Add Credits",
        type: "button",
        loader: addCreditsLoader,
        className: "addCreditsBtn",
        click: addEmailValidationCredits
      }
    };

    const getInitPage = async (refresh = false) => {
      refresh && setLoader(true);
      await Utils.reloadFeatureFlag(false);
      await getCreditsUsage();
      refresh && setLoader(false);
    };

    const CreditsLoaderComponent = () => {
      return (
        <div>
          <div className="creditsUsageBody">
            {Array.from({ length: 3 }, (item, index) => (
              <>
                <div className="creditsContainer">
                  {Array.from({ length: 3 }, (item, index) => (
                    <div className="card">
                      <h3 className="linear-background"></h3>
                      <p className="linear-background"></p>

                      <div className="linearProgessWrapper">
                        <div className="linearProgessLoaderBody">
                          <div className="limit linear-background" />
                          <div className="limit linear-background" />
                        </div>
                      </div>

                      <div className="buttonWrapper linear-background"></div>
                    </div>
                  ))}
                  <div class="add-creditsLoader linear-background"></div>
                </div>
                <hr class="creditsSeparator" />
              </>
            ))}
          </div>
        </div>
      );
    };

    const creditsButtonObj = {
      credits: {
        id: "credits",
        name: "credits",
        btnText: "Add More Credits",
        type: "button",
        className: "emailCreditsBtn",
        click: navigateToPricing
      },
      emailSendingLimits: {
        id: "emailSendingLimits",
        name: "emailSendingLimits",
        btnText: "Increase Email Sending Limit",
        type: "button",
        className: "emailCreditsBtn",
        click: navigateToPricing
      },
      emailValidationLimits: {
        id: "emailValidationLimits",
        name: "emailValidationLimits",
        btnText: "Add Validation Credits",
        type: "button",
        className: "emailCreditsBtn",
        click: addEmailValidationCredits
      },
      callingLimits: {
        id: "callingLimits",
        name: "callingLimits",
        btnText: "Add Calling Credits",
        type: "button",
        className: "emailCreditsBtn",
        click: buyCredits
      },
      phoneLookupLimits: {
        id: "phoneLookupLimits",
        name: "phoneLookupLimits",
        btnText: "Add Phone Number Lookup Credits",
        type: "button",
        className: "emailCreditsBtn",
        click: navigateToPricing
      },
      liOutReachLimits: {
        id: "liOutReachLimits",
        name: "liOutReachLimits",
        btnText: "Increase LinkedIn Outreach Limits",
        type: "button",
        className: "emailCreditsBtn",
        click: navigateToPricing
      },
      snImportLimits: {
        id: "liOutReachLimits",
        name: "liOutReachLimits",
        btnText: "Increase Sales Navigator Import Limits",
        type: "button",
        className: "emailCreditsBtn",
        click: navigateToPricing
      },
      deepResearchLimits: {
        id: "deepResearchLimits",
        name: "deepResearchLimits",
        btnText: "Add More Deep Research Credits",
        type: "button",
        className: "deepResearchBtn",
        click: navigateToPricing
      }
    };

    const creditsData = {
      emailCreditsData: [
        {
          id: "credits",
          heading: "Credits",
          paraContent:
            "Used to access emails, phones, and deep research from Discover or LinkedIn - all with one flexible credit system.",
          type: "text",
          btnObj: creditsButtonObj?.credits,
          usageLimits: [
            {
              id: "monthly",
              heading: "Monthly",
              tooltip: isFreeUser ? (
                "Your credits reset on the 1st of every month. No billing cycle—just free credits every month."
              ) : (
                <>
                  Credits reset monthly based on your billing date.
                  <span
                    style={{
                      color: "#3b82f6",
                      textDecoration: "underline",
                      cursor: "pointer"
                    }}
                    onClick={() => history.push("/settings/billing")}
                  >
                    {" "}
                    Check your billing details →
                  </span>
                </>
              ),
              classNameText: "monthlyEmailCredits",
              totalCredits: data?.creditsEligible || 0,
              remainingCredits: data?.creditsRemaining || 0,
              readOnly: true
            }
          ]
        },
        {
          id: "emailValidationCredits",
          heading: "Email Validation Credits (Pay-as-you-go)",
          paraContent:
            "Used to validate email addresses before sending emails. Powered by ZeroBounce email validation service.",
          type: "text",
          btnObj: creditsButtonObj.emailValidationLimits,
          usageLimits: [
            {
              id: "lifetime",
              heading: "Lifetime",
              tooltip:
                "Your validation credits are pay-as-you-go and remain valid until used.",
              classNameText: "emailValidation",
              totalCredits: data?.emailValidationCreditsRemaining || 0,
              remainingCredits: data?.emailValidationCreditsRemaining || 0,
              readOnly: true
            }
          ]
        },
        {
          id: "emailMonthlyLimit",
          heading: "Email sending limit",
          paraContent:
            "The maximum cold emails & follow-ups a user can send per month from Salesgear.",
          type: "text",
          btnObj: creditsButtonObj.emailSendingLimits,
          usageLimits: [
            {
              id: "emailSendingLimit",
              heading: "Monthly",
              tooltip:
                "Your monthly email sending limit resets on the 1st of every month.",
              classNameText: "monthlyEmailSendCredits",
              totalCredits: data?.monthlyEmailSendingLimit || 0,
              remainingCredits:
                data?.monthlyEmailSendingLimit - data?.monthlyEmailSent < 0
                  ? 0
                  : data?.monthlyEmailSendingLimit - data?.monthlyEmailSent,
              readOnly: true
            }
          ]
        },
        {
          id: "callCredits",
          heading: "Calling Credits",
          paraContent:
            "Used to make calls anywhere in the world using Salesgear's built-in dialer.",
          type: "text",
          btnObj: creditsButtonObj?.callingLimits,
          usageLimits: [
            {
              id: "callingMinCanadaUs",
              heading: "Free calling minutes left for US/Canada",
              tooltip: isFreeUser
                ? "Includes 60 free minutes to US/Canada — valid for lifetime."
                : planName.toLowerCase() === "pro"
                ? "Includes 500 free minutes to US/Canada every month. Resets based on your billing cycle."
                : "Includes 100 free minutes to US/Canada every month. Resets based on your billing cycle.",
              totalCredits: data?.usFreeCallingMinutesEligible || 0,
              classNameText: "callingCanadaUs",
              remainingCredits:
                data?.usFreeCallingMinutesEligible -
                  data?.usFreeCallingMinutesUsed <
                0
                  ? 0
                  : data?.usFreeCallingMinutesEligible -
                    data?.usFreeCallingMinutesUsed,
              readOnly: true
            },
            {
              id: "callCreditsRemaining",
              heading: "Call credits remaining",
              tooltip:
                "Your calling credits are pay-as-you-go and remain valid until used.",
              totalCredits:
                data?.dialerCreditsRemaining < 0
                  ? 0
                  : parseFloat(data?.dialerCreditsRemaining)?.toFixed(4),
              classNameText: "callCreditsRemaining",
              showProgress: false,
              showRemaining: false,
              remainingCredits:
                data?.dialerCreditsRemaining < 0
                  ? 0
                  : parseFloat(data?.dialerCreditsRemaining)?.toFixed(4),
              text: `$${+(data?.dialerCreditsRemaining < 0
                ? 0
                : parseFloat(data?.dialerCreditsRemaining)?.toFixed(4))}`,
              readOnly: true
            }
          ]
        }
      ],

      linkedinCreditsData: [
        {
          id: "linkedinCredits",
          heading: "LinkedIn Outreach Limits",
          paraContent:
            "Used for automated LinkedIn outreach in your sequences.",
          type: "text",
          btnObj: creditsButtonObj?.liOutReachLimits,
          subLimits: [
            {
              subHeading: "Connection Requests",
              usageLimits: [
                {
                  id: "monthly",
                  heading: "Monthly",
                  tooltip: isFreeUser
                    ? "Your monthly limit reset on the 1st of every month."
                    : "Your limit reset on your billing cycle date.",
                  classNameText: "requestMonthly",
                  totalCredits: data?.liMonthlyInviteEligible || 0,
                  remainingCredits:
                    data?.liMonthlyInviteEligible - data?.liMonthlyInviteUsed <
                    0
                      ? 0
                      : data?.liMonthlyInviteEligible -
                        data?.liMonthlyInviteUsed,
                  readOnly: true
                },
                {
                  id: "daily",
                  heading: "Daily",
                  tooltip: "Your daily limit reset at 12 AM PST.",
                  classNameText: "requestDaily",
                  totalCredits: data?.maxInvitePerDay || 0,
                  remainingCredits:
                    data?.maxInvitePerDay - data?.liDailyInviteUsed < 0
                      ? 0
                      : data?.maxInvitePerDay - data?.liDailyInviteUsed,
                  readOnly: true
                }
              ]
            },
            {
              subHeading: "Messages",
              usageLimits: [
                {
                  id: "monthly",
                  heading: "Monthly",
                  tooltip: isFreeUser
                    ? "Your monthly limit reset on the 1st of every month."
                    : "Your limit reset on your billing cycle date.",
                  classNameText: "messageMonthly",
                  totalCredits: data?.liMonthlyMessageEligible || 0,
                  remainingCredits:
                    data?.liMonthlyMessageEligible -
                      data?.liMonthlyMessageUsed <
                    0
                      ? 0
                      : data?.liMonthlyMessageEligible -
                        data?.liMonthlyMessageUsed,
                  readOnly: true
                },
                {
                  id: "daily",
                  heading: "Daily",
                  tooltip: "Your daily limit reset at 12 AM PST.",
                  classNameText: "messageDaily",
                  totalCredits: data?.maxMessagesPerDay || 0,
                  remainingCredits:
                    data?.maxMessagesPerDay - data?.liDailyMessageUsed < 0
                      ? 0
                      : data?.maxMessagesPerDay - data?.liDailyMessageUsed,
                  readOnly: true
                }
              ]
            }
          ]
        },
        {
          id: "salesNavigator",
          heading: "LinkedIn Sales navigator import",
          paraContent:
            "Used for extracting contacts from LinkedIn Sales Navigator search results.",
          type: "text",
          btnObj: creditsButtonObj?.snImportLimits,
          usageLimits: [
            {
              id: "importLimit",
              heading: "Import Limit",
              tooltip: `Allows ${Utils.formatNumberToStringInUSFormat(
                data?.snImportEligible
              )} Sales Navigator import. Each import counts as one. ${
                isFreeUser
                  ? "Limit does not reset."
                  : "Resets based on your billing cycle."
              }`,
              classNameText: "snImport",
              totalCredits: data?.snImportEligible || 0,
              remainingCredits:
                data?.snImportEligible - data?.snImportUsed < 0
                  ? 0
                  : data?.snImportEligible - data?.snImportUsed,
              readOnly: true
            },
            {
              id: "contactsImported",
              heading: "Contacts Imported",
              tooltip: `Allows up to ${Utils.formatNumberToStringInUSFormat(
                data?.snContactsEligible
              )} contact imports in total from Sales Navigator. ${
                isFreeUser
                  ? "Limit does not reset."
                  : "Resets based on your billing cycle."
              }`,
              classNameText: "snContactImport",
              totalCredits: data?.snContactsEligible || 0,
              remainingCredits:
                data?.snContactsEligible - data?.snContactsUsed < 0
                  ? 0
                  : data?.snContactsEligible - data?.snContactsUsed,
              readOnly: true
            }
          ]
        }
      ]
    };

    useEffect(() => {
      getInitPage();
    }, []);

    return (
      <div className="creditsUsageSection">
        <div className="creditsUsageHeader">
          <h6 className="titleHeader">Credit Usage</h6>
          <div className="flexEnd">
            <Button {...buttonObject["refresh"]} />
          </div>
        </div>
        <div className="creditsUsageBody">
          {creditsCardLoader ? (
            <CreditsLoaderComponent />
          ) : (
            <>
              <div className="creditsContainer">
                <EmailCredits
                  creditsData={creditsData}
                  creditsCardLoader={creditsCardLoader}
                />
              </div>
              <hr class="creditsSeparator" />
              <div className="creditsContainer">
                <LinkedinCredits
                  creditsData={creditsData}
                  creditsCardLoader={creditsCardLoader}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  })
);

export default CreditsUsage;
